import _ from 'underscore';
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { mergeClasses } from '@fluentui/react-components';

import { withControlErrorHandling } from '@bingads-webui-campaign-react/error-handling';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { FormErrorGroup, UnmappedFormErrors } from '@bingads-webui-campaign-react/form-error';
import { Collapse, useCollapseState, collapseExpander, getPanelErrorProps as getExtraProps } from '@bingads-webui-campaign/wizard-jumbo-card';
import { useFormService, FORM_STATES } from '@bingads-webui-fluent-component/form';
import { ValidationGroup } from '@bingads-webui-react/validation';
import { DateSettingCard } from '@bingads-webui-campaign-react/linkedin-budget';
import { MemberTokenWarning } from '@bingads-webui-campaign-react/linkedin-shared-components';

import { useLinkedInAdGroupSettingsStyles } from './styles/linkedin-adgroup-settings-styles';
import { AdGroupNameCard } from './cards/adgroup-name-card';

const UnmappedFormServerErrors = withControlErrorHandling(UnmappedFormErrors);

const AdGroupCards = ['budget', 'date-setting'];

export const LinkedInAdGroupSettings = ({ data, error, onDataChange }) => {
  const classes = useLinkedInAdGroupSettingsStyles();

  const { serverErrors = [] } = error || {};

  const { state } = useFormService();
  const { context: { saveFailed } } = state;
  const isSaving = state.matches(FORM_STATES.SAVING);

  const showAllValidationErrors = !_.isEmpty(serverErrors) || saveFailed;
  const areBeaconsActive = showAllValidationErrors && !isSaving;

  const [
    allCards,
    expandedCards,
    setExpandedCards,
  ] = useCollapseState(AdGroupCards, AdGroupCards);

  const expandCard = useMemo(() =>
    collapseExpander({ setActiveKeys: setExpandedCards }), [setExpandedCards]);

  const onValidate = useCallback((result) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        ...result.data,
      }),
      isValid: result.isValid,
    });
  }, [onDataChange]);

  if (!data) {
    return null;
  }

  const { isMemberTokenRevoked } = data || {};

  return (
    <div className={mergeClasses('li-adgroup-settings-step', classes.cardContainer)}>
      {isMemberTokenRevoked && <MemberTokenWarning /> }
      <ActuationGroup actuationProps={{ isActive: areBeaconsActive }}>
        <AttentionBeaconGroup>
          <FormErrorGroup formErrorProps={{ errors: serverErrors, showAllValidationErrors }}>
            <ValidationGroup validationProps={{ data, onValidate }}>
              <UnmappedFormServerErrors className="unmapped-server-errors" />
              <Collapse
                className={classes.cardContainer}
                allPanelItems={allCards}
                activeKeys={expandedCards}
                setActiveKeys={setExpandedCards}
                skipItemsTypeCheck
              >
                <AdGroupNameCard
                  key="adgroup-name"
                  className="adgroup-name-card"
                  data={data}
                  attentionBeaconProps={{ getExtraProps }}
                  actuationProps={{
                    expandKey: 'adgroup-name',
                    expand: expandCard,
                  }}
                />
                <DateSettingCard
                  key="date-setting"
                  className="date-setting-card"
                  data={data}
                  attentionBeaconProps={{ getExtraProps }}
                  actuationProps={{
                    expandKey: 'date-setting',
                    expand: expandCard,
                  }}
                />
              </Collapse>
            </ValidationGroup>
          </FormErrorGroup>
        </AttentionBeaconGroup>
      </ActuationGroup>
    </div>
  );
};

LinkedInAdGroupSettings.propTypes = {
  data: PropTypes.shape({}).isRequired,
  error: PropTypes.shape({}).isRequired,
  onDataChange: PropTypes.func.isRequired,
};
